import React, { useEffect, useState, useCallback } from "react";
import { ArrowsClockwise, Lightning, Trophy, GitDiff, Baseball, Basketball } from "@phosphor-icons/react";
import api from "../lib/api";
import BigRoad from "../components/BigRoad";

export default function BigRoadPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sport, setSport] = useState("mlb");   // "mlb" | "wnba"
  const [mode, setMode] = useState("actual");  // "actual" | "theoretical" | "diff"
  const [analystFilter, setAnalystFilter] = useState("both"); // "both" | "charlie" | "dakota"

  const load = useCallback(async (currentSport, currentMode) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/admin/strategy/big-road?sport=${currentSport}&mode=${currentMode}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load Big Road");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(sport, mode); }, [load, sport, mode]);

  const teams = data?.tracked_teams || [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="bigroad-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Admin · Streak Map</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">BIG ROAD</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl">
          Baccarat-style W/L sequence for every tracked team — MLB and WNBA, Charlie and Dakota.
          <span className="text-red-500"> Red dots</span> = wins,
          <span className="text-blue-400"> blue dots</span> = losses.
          A <span className="text-white font-bold">white ring</span> around a dot = pick initiated; 2-letter label inside is
          {" "}<span className="text-white font-mono-d">WC/LC</span>/<span className="text-white font-mono-d">WD/LD</span>/<span className="text-white font-mono-d">WB/LB</span>.
        </p>
      </div>

      {/* Sport + Mode toggles */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden shadow-lg" data-testid="bigroad-sport-tabs">
            <button
              onClick={() => setSport("mlb")}
              data-testid="bigroad-sport-mlb"
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all ${
                sport === "mlb"
                  ? "bg-[#007AFF] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#007AFF]/15 text-[#5BA8FF] hover:bg-[#007AFF]/30 hover:text-white"
              }`}
            >
              <Baseball size={15} weight="fill" /> MLB
            </button>
            <button
              onClick={() => setSport("wnba")}
              data-testid="bigroad-sport-wnba"
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
                sport === "wnba"
                  ? "bg-[#FF6B35] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#FF6B35]/15 text-[#FFA985] hover:bg-[#FF6B35]/30 hover:text-white"
              }`}
            >
              <Basketball size={15} weight="fill" /> WNBA
            </button>
          </div>
          <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden shadow-lg" data-testid="bigroad-mode-tabs">
            <button
              onClick={() => setMode("actual")}
              data-testid="bigroad-mode-actual"
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold transition-all ${
                mode === "actual"
                  ? "bg-[#22C55E] text-black shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#22C55E]/15 text-[#86EFAC] hover:bg-[#22C55E]/30 hover:text-white"
              }`}
            >
              <Trophy size={14} weight="fill" /> Actual
            </button>
            <button
              onClick={() => setMode("theoretical")}
              data-testid="bigroad-mode-theoretical"
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
                mode === "theoretical"
                  ? "bg-[#A855F7] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#A855F7]/15 text-[#D8B4FE] hover:bg-[#A855F7]/30 hover:text-white"
              }`}
            >
              <Lightning size={14} weight="fill" /> Theoretical
            </button>
            <button
              onClick={() => setMode("diff")}
              data-testid="bigroad-mode-diff"
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
                mode === "diff"
                  ? "bg-[#FF6B35] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#FF6B35]/15 text-[#FFA985] hover:bg-[#FF6B35]/30 hover:text-white"
              }`}
            >
              <GitDiff size={14} weight="fill" /> Diff
            </button>
          </div>
          {/* Per-analyst filter */}
          <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden shadow-lg" data-testid="bigroad-analyst-tabs">
            <button
              onClick={() => setAnalystFilter("both")}
              data-testid="bigroad-analyst-both"
              className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs uppercase tracking-widest font-bold transition-all ${
                analystFilter === "both"
                  ? "bg-white text-black shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-white/10 text-zinc-300 hover:bg-white/20 hover:text-white"
              }`}
            >Both</button>
            <button
              onClick={() => setAnalystFilter("charlie")}
              data-testid="bigroad-analyst-charlie"
              className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
                analystFilter === "charlie"
                  ? "bg-[#007AFF] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#007AFF]/15 text-[#5BA8FF] hover:bg-[#007AFF]/30 hover:text-white"
              }`}
            >Charlie only</button>
            <button
              onClick={() => setAnalystFilter("dakota")}
              data-testid="bigroad-analyst-dakota"
              className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
                analystFilter === "dakota"
                  ? "bg-[#A855F7] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                  : "bg-[#A855F7]/15 text-[#D8B4FE] hover:bg-[#A855F7]/30 hover:text-white"
              }`}
            >Dakota only</button>
          </div>
        </div>
        <button
          onClick={() => load(sport, mode)}
          disabled={loading}
          data-testid="bigroad-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Mode helper text */}
      <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-4 max-w-[860px]">
        {mode === "actual" && <>Showing only games where a pick was actually locked &amp; sent to subscribers.</>}
        {mode === "theoretical" && <>Showing every game the strategy <span className="text-white font-bold">would have</span> fired if each team had been shortlisted all season — for validation.</>}
        {mode === "diff" && <>Showing both layers — <span className="text-white font-bold">solid white ring</span> = pick was actually sent, <span className="text-zinc-200 font-bold">dashed ring</span> = strategy would have fired but we missed it (shortlisted late).</>}
      </div>

      {/* Legend */}
      <div className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="bigroad-legend">
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-zinc-400 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-red-600" /> Win</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-blue-500" /> Loss</span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 ring-2 ring-white font-bold text-white" style={{ fontSize: 7 }}>WC</span>
            Charlie · win
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 ring-2 ring-white font-bold text-white" style={{ fontSize: 7 }}>LD</span>
            Dakota · loss
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 ring-2 ring-white font-bold text-white" style={{ fontSize: 7 }}>WB</span>
            Both
          </span>
          {(mode === "theoretical" || mode === "diff") && (
            <span className="flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 font-bold text-white" style={{ fontSize: 7, outline: "2px solid #9ca3af", outlineOffset: 0, opacity: 0.5 }}>WC</span>
              Skipped (collision)
            </span>
          )}
          {mode === "diff" && (
            <span className="flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 font-bold text-white" style={{ fontSize: 7, outline: "2px dashed #fff", outlineOffset: 0 }}>WC</span>
              Missed pick (theoretical only)
            </span>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-d">
          {teams.length} {teams.length === 1 ? "team" : "teams"} tracked · Season {data?.season}
        </div>
      </div>

      {loading ? (
        <div className="bg-[#121212] border border-white/10 rounded-sm p-10 text-center text-sm text-zinc-500" data-testid="bigroad-loading">
          {mode === "theoretical" || mode === "diff" ? `Running ${sport.toUpperCase()} theoretical simulation — this can take ~5-15s on first run, then cached for 60s…` : `Loading ${sport.toUpperCase()} Big Road…`}
        </div>
      ) : error ? (
        <div className="bg-[#121212] border border-[#FF3B30]/30 rounded-sm p-10 text-center text-sm text-[#FF3B30]" data-testid="bigroad-error">{error}</div>
      ) : teams.length === 0 ? (
        <div className="bg-[#121212] border border-dashed border-white/10 rounded-sm p-10 text-center text-sm text-zinc-400" data-testid="bigroad-empty">
          No tracked {sport.toUpperCase()} teams yet. Shortlist teams on the Strategy tab to populate this view.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {teams.map((t) => (
            <BigRoad
              key={`${sport}-${t.team_id}`}
              title={t.team_name}
              subtitle={`${t.team_abbr} · ${t.in_charlie ? "Charlie" : ""}${t.in_charlie && t.in_dakota ? " + " : ""}${t.in_dakota ? "Dakota" : ""}`}
              results={t.results}
              mode={mode}
              analystFilter={analystFilter}
            />
          ))}
        </div>
      )}
    </div>
  );
}
